// The one reader of the API's Server-Sent Events streams (postSSE in
// NetworkUtils): text/event-stream, as every backoffice-service writer sends
// it, `data: {json}\n\n` per event plus `: ping\n\n` keepalive comments.
//
// An XHR hands the body over in chunks cut anywhere, mid-line included. An
// event is complete only once the blank line after it arrived, so only the
// text up to the LAST blank line is read; the rest is carried over to the next
// chunk. (Reading each chunk line by line, as postSSE used to, dropped every
// event split across two chunks: neither half parses as JSON.)

// A CR ends a line too (CRLF, CR, LF: the SSE line endings). A CR at the very
// end of the text is kept: its LF may be the first character of the next chunk.
const normalizeLineEnds = (text) => text.replace(/\r\n/g, '\n').replace(/\r(?!$)/g, '\n')

/**
 * The data of one event block: its `data:` lines joined with "\n" (one space
 * after the colon dropped, as the SSE spec says), or null when the block has
 * none (a comment such as ": ping", or an event with no data). Other fields
 * (event:, id:, retry:) are not used by the API and are ignored.
 */
function blockData(block) {
  const data = []
  block.split('\n').forEach((line) => {
    if (!line || line.startsWith(':')) return
    const colon = line.indexOf(':')
    const field = colon < 0 ? line : line.slice(0, colon)
    if (field !== 'data') return
    const value = colon < 0 ? '' : line.slice(colon + 1)
    data.push(value.startsWith(' ') ? value.slice(1) : value)
  })
  return data.length ? data.join('\n') : null
}

/**
 * Pure: split stream text into the data of its complete events and the rest
 * (the start of an event whose blank line has not arrived yet), which the
 * caller puts in front of the next chunk.
 * @param {string} text  what is buffered: the carried rest + the new chunk
 * @returns {{ events: string[], rest: string }}
 */
export function splitSseStream(text) {
  const normalized = normalizeLineEnds(text)
  const end = normalized.lastIndexOf('\n\n')
  if (end < 0) return { events: [], rest: normalized }
  const events = normalized
    .slice(0, end)
    .split('\n\n')
    .map(blockData)
    .filter((data) => data !== null)
  return { events, rest: normalized.slice(end + 2) }
}

/** An event's data as the object the API sent; undefined when it is not a JSON object. */
export function decodeSseData(data) {
  try {
    const value = JSON.parse(data)
    return value && typeof value === 'object' ? value : undefined
  } catch {
    return undefined
  }
}

// An error thrown by a caller's handler is reported the way the browser
// reports an uncaught one, without stopping the events after it.
const reportHandlerError = (err) => {
  if (typeof globalThis.reportError === 'function') globalThis.reportError(err)
  else setTimeout(() => { throw err })
}

/**
 * A stream reader for one response: push() every new piece of the body as it
 * arrives, flush() once when the body is complete (an event the server ended
 * without its blank line is still delivered).
 * @param {(event: object) => void} onEvent  each event, once, whole, in order
 * @param {object} [options]
 * @param {(data: string) => void} [options.onMalformed]  an event whose data is not a JSON object (skipped)
 * @param {(err) => void} [options.onError]  onEvent threw (the next events are still delivered)
 */
export function createSseParser(onEvent, { onMalformed, onError = reportHandlerError } = {}) {
  let rest = ''
  const deliver = (events) => {
    events.forEach((data) => {
      const event = decodeSseData(data)
      if (event === undefined) {
        onMalformed?.(data)
        return
      }
      try {
        onEvent(event)
      } catch (err) {
        onError(err)
      }
    })
  }
  return {
    push(chunk) {
      if (!chunk) return
      const out = splitSseStream(rest + chunk)
      rest = out.rest
      deliver(out.events)
    },
    flush() {
      const last = rest.replace(/\r$/, '')
      rest = ''
      const data = last.trim() ? blockData(last) : null
      if (data !== null) deliver([data])
    },
  }
}
